import { RefObject, useEffect, useMemo, useRef } from 'react';
import { ProductPriceDropType } from 'types/product.types';
import { GTM, GTMEvents } from 'utils/analytics';

export type PriceDropBadgeSurface = 'listing' | 'item_card';

export interface PriceDropBadgeProduct {
  id?: string | number;
  slug?: string;
  price?: number | string | null;
}

export interface PriceDropBadgeListTracking {
  listId: string;
  onBadgeInView: (product: PriceDropBadgeProduct, priceDrop: ProductPriceDropType) => void;
}

const buildParams = (surface: PriceDropBadgeSurface, product: PriceDropBadgeProduct, priceDrop: ProductPriceDropType, listId?: string) => ({
  surface,
  ...(listId && { item_list_id: listId }),
  ...(product.slug && { item_id: product.slug }),
  ...(product.id !== undefined && { product_id: Number(product.id) }),
  ...(product.price !== undefined && product.price !== null && { price: Number(product.price) }),
  reference_price: priceDrop.referencePrice,
  percentage: priceDrop.percentage,
});

export const trackPriceDropBadgeClick = (listId: string, product: PriceDropBadgeProduct, priceDrop: ProductPriceDropType) => {
  GTM.logEvent(GTMEvents.PRICE_DROP_BADGE_CLICK, buildParams('listing', product, priceDrop, listId));
};

/**
 * One price_drop_badge_view per visit to a list: fires when the first badged tile scrolls into
 * view. A remount or a resetKey change (new search) starts a fresh visit.
 */
export const usePriceDropBadgeListView = (listId: string, resetKey?: unknown): PriceDropBadgeListTracking =>
  useMemo(() => {
    let fired = false;

    return {
      listId,
      onBadgeInView: (product, priceDrop) => {
        if (fired) {
          return;
        }

        fired = true;
        GTM.logEvent(GTMEvents.PRICE_DROP_BADGE_VIEW, buildParams('listing', product, priceDrop, listId));
      },
    };
  }, [listId, resetKey]);

export const usePriceDropBadgeInView = (
  ref: RefObject<HTMLElement | null>,
  tracking: PriceDropBadgeListTracking | undefined,
  product: PriceDropBadgeProduct,
  priceDrop: ProductPriceDropType | null,
) => {
  const latest = useRef({ product, priceDrop });

  latest.current = { product, priceDrop };
  const hasBadge = !!priceDrop;

  useEffect(() => {
    const node = ref.current;

    if (!tracking || !hasBadge || !node || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || !latest.current.priceDrop) {
          return;
        }

        tracking.onBadgeInView(latest.current.product, latest.current.priceDrop);
        observer.disconnect();
      },
      { threshold: 0.5 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [ref, tracking, hasBadge]);
};

export const useTrackPriceDropBadgeItemCardView = (product: PriceDropBadgeProduct, priceDrop: ProductPriceDropType | null | undefined) => {
  const latest = useRef({ product, priceDrop });

  latest.current = { product, priceDrop };
  const trackedProductId = useRef<string | number | undefined>(undefined);
  const productId = product.id;
  const hasBadge = !!priceDrop;

  useEffect(() => {
    const current = latest.current;

    if (!hasBadge || !current.priceDrop || productId === undefined || trackedProductId.current === productId) {
      return;
    }

    trackedProductId.current = productId;
    GTM.logEvent(GTMEvents.PRICE_DROP_BADGE_VIEW, buildParams('item_card', current.product, current.priceDrop));
  }, [productId, hasBadge]);
};
